/* ==========================================================================
   Kizmit Designs — product page
   3D photo stage, variant picker, zoom viewer, sticky bar, size guide.
   Relies on window.Kizmit from theme.js.
   ========================================================================== */
(() => {
  'use strict';
  const K = window.Kizmit;
  if (!K) return;
  const { $, $$, clamp, mod, reduceMotion } = K;

  function setupPdp(root) {
    let data;
    try { data = JSON.parse($('[data-product-json]', root).textContent); } catch (e) { return; }

    /* ---------- Gallery stage ---------- */
    const frame = $('[data-frame]', root);
    const slides = $$('[data-slide]', root);
    const thumbs = $$('[data-thumb]', root);
    const countEl = $('[data-count]', root);
    const progress = $('[data-progress]', root);
    const n = slides.length;
    let current = 0;

    function go(i, opts = {}) {
      if (!n) return;
      const next = mod(i, n);
      if (next === current && !opts.force) return;
      const dir = next > current ? 1 : -1;
      const prev = slides[current];
      frame.classList.remove('is-zoomed');
      slides.forEach((s) => s.style.setProperty('--dir', dir));
      prev.classList.remove('is-active');
      prev.classList.add('is-leaving');
      setTimeout(() => prev.classList.remove('is-leaving'), 900);
      slides[next].classList.add('is-active');
      prev.querySelector('video')?.pause();
      thumbs.forEach((t, k) => {
        t.classList.toggle('is-active', k === next);
        t.setAttribute('aria-pressed', k === next ? 'true' : 'false');
      });
      thumbs[next]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
      current = next;
      if (countEl) countEl.textContent = String(next + 1).padStart(2, '0');
      if (progress) progress.style.setProperty('--p', ((next + 1) / n).toFixed(3));
    }
    go(0, { force: true });

    thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.thumb))));
    $('[data-prev]', root)?.addEventListener('click', () => go(current - 1));
    $('[data-next]', root)?.addEventListener('click', () => go(current + 1));

    // Tilt + glare follow the pointer; click toggles a zoom that tracks the pointer.
    if (frame) {
      let sx = null;
      let moved = false;
      frame.addEventListener('pointermove', (e) => {
        const r = frame.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;
        const py = (e.clientY - r.top) / r.height;
        frame.style.setProperty('--gx', `${(px * 100).toFixed(1)}%`);
        frame.style.setProperty('--gy', `${(py * 100).toFixed(1)}%`);
        if (frame.classList.contains('is-zoomed')) {
          const img = $('.pdp__slide.is-active img', frame);
          img?.style.setProperty('--zx', `${(px * 100).toFixed(1)}%`);
          img?.style.setProperty('--zy', `${(py * 100).toFixed(1)}%`);
        } else if (!reduceMotion && e.pointerType === 'mouse') {
          frame.style.setProperty('--ry', `${((px - 0.5) * 10).toFixed(2)}deg`);
          frame.style.setProperty('--rx', `${((0.5 - py) * 8).toFixed(2)}deg`);
        }
        if (sx !== null && Math.abs(e.clientX - sx) > 8) moved = true;
      });
      frame.addEventListener('pointerleave', () => {
        frame.style.setProperty('--rx', '0deg');
        frame.style.setProperty('--ry', '0deg');
      });
      frame.addEventListener('pointerdown', (e) => { sx = e.clientX; moved = false; });
      frame.addEventListener('pointerup', (e) => {
        if (sx === null) return;
        const dx = e.clientX - sx;
        sx = null;
        if (Math.abs(dx) > 45) { go(current + (dx < 0 ? 1 : -1)); return; }
        if (moved) return;
        const active = slides[current];
        if (!active || !active.querySelector('img')) return;
        if (e.pointerType === 'mouse') {
          const r = frame.getBoundingClientRect();
          const img = active.querySelector('img');
          img.style.setProperty('--zx', `${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}%`);
          img.style.setProperty('--zy', `${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
          frame.classList.toggle('is-zoomed');
          frame.style.setProperty('--rx', '0deg');
          frame.style.setProperty('--ry', '0deg');
        } else {
          openLightbox(current);
        }
      });
    }

    // Arrow keys move through photos while the stage is on screen.
    document.addEventListener('keydown', (e) => {
      if (lightbox?.open || sizeGuide?.open) return;
      if (/INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || '')) return;
      const r = root.getBoundingClientRect();
      if (r.top > window.innerHeight * 0.5 || r.bottom < window.innerHeight * 0.5) return;
      if (e.key === 'ArrowRight') go(current + 1);
      if (e.key === 'ArrowLeft') go(current - 1);
    });

    /* ---------- Lightbox ---------- */
    const lightbox = $('[data-lightbox]', root);
    const lbTrack = $('[data-lb-track]', root);
    const lbItems = $$('[data-lb-item]', root);
    const lbCount = $('[data-lb-count]', root);
    // Map gallery index (all media) to lightbox index (images only).
    const imageIndex = slides.map((s) => (s.querySelector('img') ? 1 : 0));
    function toLbIndex(i) { let c = -1; for (let k = 0; k <= i; k += 1) c += imageIndex[k] || 0; return Math.max(0, c); }
    function lbGo(i) {
      const t = clamp(i, 0, lbItems.length - 1);
      lbTrack.scrollTo({ left: t * lbTrack.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    function openLightbox(i) {
      if (!lightbox || !lbItems.length) return;
      lightbox.showModal();
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => { lbTrack.scrollLeft = toLbIndex(i) * lbTrack.clientWidth; });
    }
    if (lightbox) {
      $('[data-open-lightbox]', root)?.addEventListener('click', () => openLightbox(current));
      $('[data-lb-close]', lightbox).addEventListener('click', () => lightbox.close());
      lightbox.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
        $$('[data-lb-zoom]', lightbox).forEach((z) => z.classList.remove('is-zoomed'));
      });
      const lbIndex = () => Math.round(lbTrack.scrollLeft / lbTrack.clientWidth);
      $('[data-lb-prev]', lightbox).addEventListener('click', () => lbGo(lbIndex() - 1));
      $('[data-lb-next]', lightbox).addEventListener('click', () => lbGo(lbIndex() + 1));
      lbTrack.addEventListener('scroll', () => { if (lbCount) lbCount.textContent = lbIndex() + 1; }, { passive: true });
      lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); lbGo(lbIndex() + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); lbGo(lbIndex() - 1); }
      });
      $$('[data-lb-zoom]', lightbox).forEach((z) => {
        z.addEventListener('click', (e) => {
          const img = z.querySelector('img');
          const r = img.getBoundingClientRect();
          img.style.setProperty('--zx', `${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}%`);
          img.style.setProperty('--zy', `${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
          z.classList.toggle('is-zoomed');
        });
        z.addEventListener('pointermove', (e) => {
          if (!z.classList.contains('is-zoomed')) return;
          const img = z.querySelector('img');
          const r = z.getBoundingClientRect();
          img.style.setProperty('--zx', `${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}%`);
          img.style.setProperty('--zy', `${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
        });
      });
    }

    /* ---------- Size guide ---------- */
    const sizeGuide = $('[data-size-guide]', root);
    if (sizeGuide) {
      $$('[data-open-size]', root).forEach((b) => b.addEventListener('click', () => sizeGuide.showModal()));
      $('[data-size-close]', sizeGuide).addEventListener('click', () => sizeGuide.close());
      sizeGuide.addEventListener('click', (e) => { if (e.target === sizeGuide) sizeGuide.close(); });
    }

    /* ---------- Variant picker ---------- */
    const form = $('[data-pdp-form]', root);
    const idInput = $('[data-variant-id]', root);
    const addBtn = $('[data-add]', root);
    const priceNow = $('[data-price-now]', root);
    const priceWas = $('[data-price-was]', root);
    const priceSave = $('[data-price-save]', root);
    const barPrice = $('[data-bar-price]', root);
    const barVariant = $('[data-bar-variant]', root);
    const barBtn = $('[data-bar-btn]', root);
    const strings = window.theme.strings;
    let variant = data.variants.find((v) => String(v.id) === idInput?.value) || data.variants[0];

    function selectedOptions() {
      const values = variant ? [...variant.options] : [];
      $$('[data-option-input]:checked', form).forEach((input) => {
        values[Number(input.dataset.optionInput)] = input.value;
      });
      return values;
    }

    function refreshAvailability(values) {
      // Grey out values that have no available variant with the other current choices.
      $$('[data-option]', form).forEach((fs) => {
        const idx = Number(fs.dataset.option);
        $$('[data-option-input]', fs).forEach((input) => {
          const trial = [...values];
          trial[idx] = input.value;
          const ok = data.variants.some((v) => v.available && v.options.every((o, k) => o === trial[k]));
          input.closest('label').classList.toggle('is-out', !ok);
          input.closest('label').classList.toggle('is-active', input.checked);
        });
      });
    }

    function update() {
      const values = selectedOptions();
      const match = data.variants.find((v) => v.options.every((o, k) => o === values[k]));
      $$('[data-option]', form).forEach((fs) => {
        const checked = $('[data-option-input]:checked', fs);
        const label = $('[data-option-value]', fs);
        if (checked && label) label.textContent = checked.value;
      });
      refreshAvailability(values);
      if (!match) {
        addBtn.disabled = true;
        $('[data-label-default]', addBtn).textContent = strings.soldOut;
        if (barBtn) barBtn.disabled = true;
        return;
      }
      variant = match;
      idInput.value = match.id;
      addBtn.disabled = !match.available;
      $('[data-label-default]', addBtn).textContent = match.available ? strings.addToCart : strings.soldOut;
      if (barBtn) { barBtn.disabled = !match.available; barBtn.textContent = match.available ? strings.addToCart : strings.soldOut; }
      priceNow.textContent = match.price;
      priceWas.textContent = match.compare;
      priceWas.hidden = !match.compare;
      priceSave.textContent = `Save ${match.save}%`;
      priceSave.hidden = !match.save;
      if (barPrice) barPrice.textContent = match.price;
      if (barVariant) barVariant.textContent = match.options.filter((o, k) => $(`[data-option="${k}"]`, form)).join(' · ');
      if (match.media >= 0) go(match.media);
      const url = new URL(location.href);
      url.searchParams.set('variant', match.id);
      history.replaceState({}, '', url);
    }
    form?.addEventListener('change', (e) => { if (e.target.matches('[data-option-input]')) update(); });
    if (form) refreshAvailability(selectedOptions());

    // Mirror "added" state onto the curved button when the sticky bar adds.
    form?.addEventListener('submit', (e) => {
      if (e.submitter && e.submitter !== addBtn) setTimeout(() => K.markAdded(addBtn), 400);
    });

    /* ---------- Sticky bar ---------- */
    const bar = $('[data-bar]', root);
    if (bar && addBtn && 'IntersectionObserver' in window) {
      let buttonVisible = true;
      let footerVisible = false;
      const paint = () => {
        const show = !buttonVisible && !footerVisible && addBtn.getBoundingClientRect().top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (barBtn) barBtn.tabIndex = show ? 0 : -1;
      };
      new IntersectionObserver(([e]) => { buttonVisible = e.isIntersecting; paint(); }).observe(addBtn);
      const footer = $('.section-footer, footer');
      if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; paint(); }).observe(footer);
      window.addEventListener('scroll', paint, { passive: true });
    }

    /* ---------- Delivery date ---------- */
    const ship = $('[data-ship]', root);
    if (ship) {
      const addBusinessDays = (d, days) => {
        const out = new Date(d);
        let added = 0;
        while (added < days) {
          out.setDate(out.getDate() + 1);
          if (out.getDay() !== 0) added += 1; // skip Sundays
        }
        return out;
      };
      const fmt = (d) => d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
      const min = addBusinessDays(new Date(), Number(ship.dataset.min));
      const max = addBusinessDays(new Date(), Number(ship.dataset.max));
      $('[data-ship-date]', ship).textContent = `${fmt(min)} – ${fmt(max)}`;
    }

    /* ---------- Remember this product ---------- */
    try {
      const KEY = 'kizmit:recent';
      const list = JSON.parse(localStorage.getItem(KEY) || '[]').filter((h) => h !== data.handle);
      list.unshift(data.handle);
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, 16)));
    } catch (e) { /* storage blocked */ }
  }

  function init(scope) {
    K.fresh('[data-pdp]', scope).forEach(setupPdp);
  }
  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
